import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { shared } from "../styles";
import { Absence, Employee } from "@pentacode/core";
import "./avatar";
import { singleton } from "../lib/singleton";
import { AbsenceDialog } from "./absence-dialog";
import { router } from "../init";
import { defaultRangeFormatter, getAbsenceRequestedDays, userFormatSettings } from "../utils/temporal";
import type { DurationFormatOptions } from "@formatjs/intl-durationformat/src/types";

export type EmployeeAbsenceListItem = { employee: Employee; absences: Absence[]; commonWorkDays: number[] };

@customElement("ptc-employee-absence-list")
export class EmployeeAbsenceList extends LitElement {
    @property({ type: String })
    mode: "detailed" | "compact" = "detailed";

    @property({ type: Array, attribute: false })
    employeeAbsences: EmployeeAbsenceListItem[] = [];

    @singleton("ptc-absence-dialog")
    private _absenceDialog: AbsenceDialog;

    static styles = [
        shared,
        css`
            :host {
                --absence-duration-color: var(--blue);

                display: grid;
            }

            [part="employee-list"] {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }

            [part="employee-avatar"] {
                place-self: start center;
                grid-column: 1;
            }

            [part="employee"] {
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }

            [part="employee-name"] {
                font-size: 1.15em;
                font-weight: 600;
                grid-column: 2;
            }

            [part="employee-header"] {
                display: grid;
                grid-template-columns: min-content auto;
                gap: 0.5rem;
                place-items: center start;
                position: relative;
                cursor: pointer;
                transition: color 0.2s ease-in-out;
            }

            [part="employee-header"]:hover {
                color: var(--blue);
            }
            [part="employee-header"] > * {
                grid-row: 1;
            }

            [part="absence-list"] {
                list-style: none;
                padding: 0;
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }

            [part="absence-item"] {
                box-sizing: border-box;
                padding-inline-end: 0.5rem;
                border-radius: 0.5rem;
                cursor: pointer;
                display: grid;
                grid-template-columns: auto auto;
                place-items: center start;
                gap: 0.5rem;
                transition: background-color 0.2s ease-in-out;
            }

            [part="absence-item"]:hover {
                background-color: var(--shade-1);
            }

            [part="absence-dates"] {
                place-self: center end;
            }

            [part="absence-duration"] {
                display: inline-grid;
                background-color: var(--absence-duration-color);
                color: white;
                padding: 0.25rem 0.5rem;
                border-radius: 0.25rem;
                place-items: center;
                height: 100%;
                box-sizing: border-box;
            }

            [slot="empty-message"] {
                padding: 1rem;
            }
        `,
    ];

    handleAbsenceClick(absence: Absence) {
        this._absenceDialog
            .show(absence)
            .then((result) => {
                if (result?.updated || result?.deleted) {
                    this.employeeAbsences = this.employeeAbsences.filter(
                        (item) => !item.absences.find((a) => a.id === absence.id)
                    );
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }

    connectedCallback(): void {
        super.connectedCallback();
    }

    render() {
        return html`
            ${this.employeeAbsences.length === 0
                ? html`<slot name="empty-message">
                      <p>Keine Abwesenheiten gefunden!</p>
                  </slot>`
                : html`
                      <ul part="employee-list">
                          ${this.employeeAbsences?.map(
                              (item) => html`
                                  <li part="employee">
                                      <header
                                          part="employee-header"
                                          @click=${() => router.go(`employees/${item.employee.id}/absences`)}
                                      >
                                          <ptc-avatar .employee=${item.employee} part="employee-avatar"></ptc-avatar>
                                          <span part="employee-name">${item.employee.name}</span>
                                      </header>
                                      <ul part="absence-list">
                                          ${item.absences.map((absence) => {
                                              const absenceRequestedDays = getAbsenceRequestedDays(
                                                  absence,
                                                  item.commonWorkDays
                                              );
                                              return html`
                                                  <li
                                                      part="absence-item"
                                                      @click=${() => this.handleAbsenceClick(absence)}
                                                  >
                                                      <span part="absence-duration">
                                                          ${absenceRequestedDays > 0
                                                              ? Temporal.Duration.from({
                                                                    days: absenceRequestedDays,
                                                                }).toLocaleString(userFormatSettings.locale, {
                                                                    style: "long",
                                                                } as DurationFormatOptions)
                                                              : "? Tage"}
                                                      </span>

                                                      <span part="absence-dates">
                                                          <time
                                                              >${defaultRangeFormatter(
                                                                  absence.start,
                                                                  absence.end
                                                              )}</time
                                                          >
                                                      </span>
                                                  </li>
                                              `;
                                          })}
                                      </ul>
                                  </li>
                              `
                          )}
                      </ul>
                  `}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-employee-absence-list": EmployeeAbsenceList;
    }
}
